import React, { useState } from 'react';
import { array, number, func, object, bool } from 'prop-types';
import { Table } from 'antd';
import { includes, isEmpty, filter, split, upperCase, head, tail, get, map } from 'lodash';
import moment from 'moment';
import {
    Text,
    TableCount,
    TableContainer,
    Icon,
    HasPermissions,
    FlexContainer,
    Modal,
    IconContainer,
    ConfirmFooterButtons,
    Button
} from '../../common';
import { withApollo } from 'react-apollo';
import { DELETE_QR } from '../../../queries';

const getNewFormat = (item) => {
    const formatTypes = ['Bandeja', 'Puerta', 'Mueble', 'Ubicación', 'Pallet'];
    const foundFormat = head(filter(formatTypes, (format) => includes(item, format)));
    if (!isEmpty(foundFormat)) return `${upperCase(head(foundFormat))}${tail(split(item, ' '))}`;
    return '';
};

const qrIcons = ({ numqr, params, item, disableTrash, setItemStorage, setModalVisible }) => [
    {
        iconName: 'printer',
        onClick: () => window.open(`/positions-qr/${numqr}?${params}`, '_blank'),
        permission: 'darkstore-page:print-labels',
        color: '#a4c735',
        cursor: 'pointer'
    },
    {
        iconName: 'trash',
        onClick: async () => {
            if (!disableTrash) {
                setItemStorage(item);
                setModalVisible(true);
            }
        },
        permission: 'darkstore-page:delete-qr',
        color: disableTrash ? '#eaeaea' : '#a4c735',
        disabled: disableTrash,
        cursor: disableTrash ? 'not-allowed' : 'pointer'
    }
];

const columns = (trashActions) => [
    {
        title: 'QR',
        dataIndex: 'id',
        className: 'bo-table-header',
        width: 60,
        render: (_value, item) => {
            const { numqr, placeLabel, place, aisle, positionLabel, position, sectorPicking } = item;
            const newPositionFormat = getNewFormat(isEmpty(positionLabel) ? position : `${positionLabel} ${position}`);
            const newPlaceFormat = getNewFormat(isEmpty(placeLabel) ? place : `${placeLabel} ${place}`);
            const paramPlace = isEmpty(newPlaceFormat) ? '' : `place=${newPlaceFormat}`;
            const paramPosition = isEmpty(newPositionFormat) ? '' : `position=${newPositionFormat}`;
            const paramAisle = isEmpty(aisle) ? '' : `aisle=P${aisle}`;
            const paramSectorPicking = isEmpty(sectorPicking) ? '' : `sectorPicking=${sectorPicking}`;
            const params = `${paramSectorPicking}&${paramPlace}&${paramAisle}&${paramPosition}`;

            return (
                <FlexContainer justify='space-between'>
                    { map(qrIcons({ numqr, params, item, ...trashActions }), ({ permission, iconName, ...properties }) => (
                        <HasPermissions
                            to={[permission]}
                            otherwise={'-'}
                        >
                            <Icon
                                {...properties}
                                name={iconName}
                                size='19'
                            />
                        </HasPermissions>
                    )) }
                </FlexContainer>
            );
        }
    },
    {
        title: 'ID QR',
        sorter: true,
        dataIndex: 'numqr',
        className: 'bo-table-header',
        render: (item) => {
            return (<Text text={item} />);
        }
    },
    {
        title: 'Sector de Picking',
        sorter: true,
        dataIndex: 'sectorPicking',
        className: 'bo-table-header',
        render: (value, item) => {
            return <Text text={get(item, 'sectorPicking')} />;
        }
    },
    {
        title: 'Código SAP',
        sorter: true,
        dataIndex: 'sku',
        className: 'bo-table-header',
        render: (item) => (
            <Text text={item} />
        )
    },
    {
        title: 'EAN',
        sorter: true,
        dataIndex: 'ean',
        className: 'bo-table-header',
        render: (item) => (
            <Text text={item} />
        )
    },
    {
        title: 'Ubicación primer nivel',
        dataIndex: 'position',
        className: 'bo-table-header',
        width: 180,
        sorter: true,
        render: (_value, { positionLabel, position }) => {
            const label = isEmpty(positionLabel) ? '' : `${positionLabel} `;
            return <Text text={isEmpty(position) ? '' : `${label}${position}`} />;
        }
    },
    {
        title: 'Ubicación segundo nivel',
        dataIndex: 'place',
        className: 'bo-table-header',
        width: 185,
        sorter: true,
        render: (_value, { placeLabel, place }) => {
            const label = isEmpty(placeLabel) ? '' : `${placeLabel} `;
            return <Text text={isEmpty(place) ? '' : `${label}${place}`} />;
        }
    },
    {
        title: 'Ubicación tercer nivel',
        dataIndex: 'tray',
        className: 'bo-table-header',
        width: 180,
        sorter: true,
        render: (_value, { trayLabel, tray }) => {
            const label = isEmpty(trayLabel) ? '' : `${trayLabel} `;
            return <Text text={isEmpty(tray) ? '' : `${label}${tray}`} />;
        }
    },
    {
        title: 'Ubicación cuarto nivel',
        dataIndex: 'aisle',
        className: 'bo-table-header',
        width: 180,
        sorter: true,
        render: (_value, { aisleLabel, aisle }) => {
            const label = isEmpty(aisleLabel) ? '' : `${aisleLabel} `;
            return <Text text={isEmpty(aisle) ? '' : `${label}${aisle}`} />;
        }
    },
    {
        title: 'Fecha de última acción del QR',
        dataIndex: 'updatedat',
        className: 'bo-table-header',
        width: 180,
        sorter: true,
        render: value => {
            return (<Text text={moment(value, 'YYYY-MM-DD HH:mm').format('DD/MM/YYYY HH:mm')} />);
        }
    }
];

const getDir = (direction) => direction === 'ascend' ? 'asc' : 'desc';

const PositionsTable = ({
    data,
    count,
    getPositionsDarkStore,
    cachedFilters,
    formatVariables,
    setSort,
    loadingTable,
    disableTrash,
    client,
    refetchQuery,
    ...props
}) => {
    const [loading, setLoading] = useState(false);
    const [itemStorage, setItemStorage] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(null);

    const CloseModalBtn = () => (
        <Button
            link
            text='Cerrar'
            style={{ bottom: '20px', position: 'absolute' }}
            onClick={() => {
                setModalVisible(false);
                setItemStorage(null);
                setDeleteSuccess(null);
            }}
        />
    );

    const modal = () => {
        const { aisleLabel, aisle, placeLabel, place, positionLabel, position, sectorPicking } = itemStorage || {};

        if (deleteSuccess === true) {
            return (
                <>
                    <Icon name='check-mark' size={90} color='#1fa02e' top={20} bottom={20} />
                    <Text
                        size={14}
                        strong
                        text='Se eliminó el QR exitosamente'
                        align='center'
                    />
                    <CloseModalBtn />
                </>
            );
        }
        if (deleteSuccess === false) {
            return (
                <>
                    <Icon name='cross' size={90} color='red' top={20} bottom={20} />
                    <Text
                        size={14}
                        strong
                        text='Hubo un error al eliminar el QR'
                        align='center'
                    />
                    <CloseModalBtn />
                </>
            );
        }
        return (
            <>
                <IconContainer color='#f38630' margin={20}>
                    <Icon name='trash' size={43} color='#ffffff' />
                </IconContainer>
                <Text
                    size={14}
                    strong
                    align='center'
                    text={`¿Estas seguro que quiere eliminar el QR del sector ${sectorPicking}${aisle ? `, ${aisleLabel} ${aisle}` : ''}${position ? `, ${positionLabel} ${position}` : ''}${place ? `, ${placeLabel} ${place}` : ''}?`}
                />
                <ConfirmFooterButtons
                    onCancel={() => {
                        setModalVisible(false);
                        setItemStorage(null);
                    }}
                    confirmLabel='Confirmar'
                    onConfirm={async () => {
                        await client.mutate({
                            mutation: DELETE_QR,
                            variables: { qr: get(itemStorage, 'numqr') }
                        }).then(({ data }) => {
                            setDeleteSuccess(true);
                            refetchQuery();
                        }).catch((_error) => {
                            setDeleteSuccess(false);
                        });
                    }}
                />
            </>
        );
    };

    return <TableContainer
        {...props}
        style={{ overflow: 'hidden', overflowY: 'auto' }}
    >
        {({ onRow }) => (
            <>
                <TableCount count={count} />
                <Table
                    className='bo-table order-table'
                    size='small'
                    columns={columns({ disableTrash, setItemStorage, setModalVisible })}
                    dataSource={data}
                    pagination={false}
                    rowKey={record => record.id}
                    loading={loading || loadingTable}
                    onChange={async (_pagination, _filters, sorter) => {
                        setLoading(true);
                        const sort = !isEmpty(get(sorter, 'field'))
                            ? { field: get(sorter, 'field'), dir: getDir(get(sorter, 'order')) }
                            : {};
                        const variables = formatVariables(cachedFilters, sort);

                        await getPositionsDarkStore(variables, false);
                        setTimeout(() => setLoading(false), 1000);
                        setSort(sort);
                    }}
                />
                <Modal visible={modalVisible} withoutHeader>
                    <FlexContainer align='center' direction='column' height={250}>
                        { modal() }
                    </FlexContainer>
                </Modal>
            </>
        )}
    </TableContainer>;
};

PositionsTable.propTypes = {
    data: array,
    count: number,
    getPositionsDarkStore: func,
    formatVariables: func,
    cachedFilters: object,
    setSort: func,
    loadingTable: bool
};

PositionsTable.defaultProps = {
    count: 10
};

export default withApollo(PositionsTable);
